<template>
    <v-autocomplete
        ref="autocomplete"
        v-bind="$attrs"
        v-on="$listeners"
        :value="value"
        :items="filteredItems"
        :item-text="itemText"
        :item-value="itemValue"
        :value-comparator="valueComparator"
        :search-input="searchInput"
        :filter="postSliceFilterFunction"
        :loading="loading"
        :class="[$vnode.data.staticClass, $vnode.data.class]"
        :clearable="clearable && (!!value || !!searchInput)"
        :rules="rules"
        @update:search-input="onUpdateSearchInputDirty"
        @click:clear="onClickClear"
        :no-data-text="noDataTextValue"
    >
        <template v-for="(_, scopedSlot) of passScopedSlots" v-slot:[scopedSlot]="scope">
            <slot :name="scopedSlot" v-bind="scope"/>
        </template>
        <template v-for="(_, slot) of $slots" v-slot:[slot]>
            <slot :name="slot"></slot>
        </template>
    </v-autocomplete>
</template>

<script>
import _ from "lodash";
import {deepEqual} from "vuetify/lib/util/helpers";

export default {
    name: "VAutocompleteBigData",
    props: {
        value: {},
        items: {
            type: [Object, Array],
        },
        debounce: {
            type: Number,
            default: 600,
        },
        listSize: {
            type: Number,
            default: 100,
        },
        itemValue: {
            default: 'id',
        },
        itemText: {
            default: 'name',
        },
        valueComparator: {
            type: Function,
            default: deepEqual,
        },
        clearable: {
            type: Boolean,
        },
        loading: {
            type: Boolean,
        },
        noDataText: {
            type: String,
        },
        filter: {},
        sliceFunction: {
            type: Function,
        },
        postSliceFilterFunction: {
            type: Function,
        },
        sortBy: {
            type: String,
        },
        rules: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            searchInput: '',
            isSearchInputDirty: false,
            isMounted: false,
        };
    },
    mounted() {
        this.setCachedItem(this.currentItem);
        this.isMounted = true;
    },
    computed: {
        isMenuActive() {
            return this.isMounted && this.$refs?.autocomplete?.isMenuActive;
        },
        /**
         * @returns {DebouncedFunc<default.methods.onUpdateSearchInput>}
         */
        onUpdateSearchInputDebounced() {
            return _.debounce(this.onUpdateSearchInput, this.debounce);
        },
        applySliceFunction() {
            return this.sliceFunction || this.sliceFunctionDefault;
        },
        defaultList() {
            return _(this.items)
                    .toArray()
                    .orderBy(v => this.sortBy && Object.hasOwn(v, this.sortBy) ? v[this.sortBy] : v[this.itemText])
                    .take(this.listSize)
                    .value();
        },
        filteredItems() {
            if (!this.isMenuActive) {
                return [];
            }
            const searchInput = this.searchInput.toString().trim().toLocaleLowerCase();
            if (this.currentItem && (searchInput === this.currentItem[this.itemText].toString().trim().toLocaleLowerCase())) {
                return this.defaultList;
            }
            return searchInput ? this.applySliceFunction(this.items, searchInput) : this.defaultList;
        },
        indexedItems() {
            return _.keyBy(this.items, this.itemValue);
        },
        currentItem() {
            const value = this.value;
            const itemValue = this.itemValue;
            const valueComparator = this.valueComparator;
            const item = _.isArray(this.items) ? this.indexedItems[value] : (this.items && this.items[value]);
            return (item && valueComparator(item[itemValue], value) && item) 
                      || _.find(this.items, item => valueComparator(item[itemValue], value)) 
                      || (value && {[itemValue]: value, [this.itemText]: `ID# ${value}`}) 
                      || undefined;
        },
        noDataTextValue() {
            return (this.loading || this.isSearchInputDirty) ? 'Загрузка данных' : (this.noDataText || this.$vuetify.lang.t('$vuetify.noDataText'));
        },
        passScopedSlots() {
            return _.omit(this.$scopedSlots, _.keys(this.$slots));
        }
    },
    watch: {
        currentItem: {
            handler(v) {
                this.setCachedItem(v);
                if (v) {
                    this.onUpdateSearchInputDebounced.cancel();
                    this.searchInput = v[this.itemText] || '';
                }
                /** без этого в selectedItems остается первоначальное значение */
                this.$refs?.autocomplete?.setSelectedItems();
                this.$nextTick(() => {
                    this.validate(true);
                });
            },
        },
    },
    methods: {
        setCachedItem(item) {
            if (!this.$refs?.autocomplete) {
                return;
            }
            this.$refs.autocomplete.cachedItems = item ? [item] : [];
        },
        onUpdateSearchInputDirty(v) {
            this.isSearchInputDirty = true;
            this.onUpdateSearchInputDebounced(v);
            if (!v) {
                this.onUpdateSearchInputDebounced.flush()
            }
        },
        onUpdateSearchInput(v) {
            if (!_.isNil(v)) {
                this.searchInput = v;
                this.isSearchInputDirty = false;
            }
        },
        onClickClear() {
            this.searchInput = '';
            this.isSearchInputDirty = false;
        },
        sliceFunctionDefault(items, searchInput) {
            const itemText = this.itemText;
            const res = _(items)
                .filter(v => v && v[itemText] && (v[itemText].toLocaleLowerCase().indexOf(searchInput) > -1))
                .take(this.listSize)
                .value();
            return res;
        },
        validate(...args) {
            this.$refs?.autocomplete?.validate(...args);
        },
    },
}
</script>

<style scoped>

</style>
